import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';

/* ── Custom always-visible scrollbar ─────────────────────────────────────── */
const PurpleGoldScrollbar = ({ scrollRef, containerHeight = 520 }) => {
  const [thumbTop, setThumbTop]       = useState(0);
  const [thumbHeight, setThumbHeight] = useState(40);
  const [dragging, setDragging]       = useState(false);
  const dragStartY  = useRef(0);
  const dragStartST = useRef(0);
  const trackRef    = useRef(null);

  const update = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const ratio      = el.clientHeight / el.scrollHeight;
    const trackH     = el.clientHeight;
    const thumbH     = Math.max(28, trackH * ratio);
    const maxScroll  = el.scrollHeight - el.clientHeight;
    const maxThumb   = trackH - thumbH;
    const top        = maxScroll > 0 ? (el.scrollTop / maxScroll) * maxThumb : 0;
    setThumbHeight(thumbH);
    setThumbTop(top);
  }, [scrollRef]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    update();
    el.addEventListener('scroll', update);
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => { el.removeEventListener('scroll', update); ro.disconnect(); };
  }, [scrollRef, update]);

  const onMouseDown = (e) => {
    e.preventDefault();
    setDragging(true);
    dragStartY.current  = e.clientY;
    dragStartST.current = scrollRef.current?.scrollTop || 0;
  };

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const el = scrollRef.current;
      if (!el) return;
      const dy        = e.clientY - dragStartY.current;
      const trackH    = el.clientHeight;
      const maxThumb  = trackH - thumbHeight;
      const ratio     = maxThumb > 0 ? dy / maxThumb : 0;
      const maxScroll = el.scrollHeight - el.clientHeight;
      el.scrollTop    = Math.max(0, Math.min(maxScroll, dragStartST.current + ratio * maxScroll));
    };
    const onUp = () => setDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [dragging, thumbHeight, scrollRef]);

  const onTrackClick = (e) => {
    if (e.target !== trackRef.current) return;
    const el = scrollRef.current;
    if (!el) return;
    const rect   = trackRef.current.getBoundingClientRect();
    const clickY = e.clientY - rect.top - thumbHeight / 2;
    const maxThumb  = el.clientHeight - thumbHeight;
    const ratio     = maxThumb > 0 ? clickY / maxThumb : 0;
    el.scrollTop    = Math.max(0, Math.min(el.scrollHeight - el.clientHeight, ratio * (el.scrollHeight - el.clientHeight)));
  };

  return (
    <div
      ref={trackRef}
      onClick={onTrackClick}
      style={{
        position: 'absolute', right: 0, top: 0, bottom: 0, width: 8,
        background: 'linear-gradient(180deg,#f3e8ff 0%,#fef9e7 100%)',
        borderRadius: 8, cursor: 'pointer', zIndex: 10,
      }}
    >
      <div
        onMouseDown={onMouseDown}
        style={{
          position: 'absolute', left: 1, right: 1,
          top: thumbTop, height: thumbHeight,
          background: 'linear-gradient(180deg,#8b5cf6 0%,#a855f7 50%,#D4AF37 100%)',
          borderRadius: 8,
          boxShadow: '0 0 8px rgba(139,92,246,0.5), 0 0 4px rgba(212,175,55,0.3)',
          cursor: dragging ? 'grabbing' : 'grab',
          transition: dragging ? 'none' : 'top 0.08s',
        }}
      />
    </div>
  );
};
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronRight, ChevronLeft, Clock, Wifi, MapPin, Quote, Calendar as CalendarIcon, ShoppingCart } from 'lucide-react';
import { HEADING, BODY, GOLD, CONTAINER, applySectionStyle } from '../lib/designTokens';
import { useCurrency } from '../context/CurrencyContext';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { useCart } from '../context/CartContext';
import { useToast } from '../hooks/use-toast';
import { renderMarkdown } from '../lib/renderMarkdown';
import { timeSlotsForCalendarDate } from '../lib/sessionCalendarSlots';
import SessionOpenDatesSlots from './SessionOpenDatesSlots';
import { resolveEffectiveOffer, isEarlyBirdActive } from '../lib/effectiveOfferPricing';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PURPLE_GRADIENTS = {
  light: 'linear-gradient(160deg, #faf8ff 0%, #f8f4ff 30%, #fdf8f3 60%, #fffcf7 100%)',
  medium: 'linear-gradient(160deg, #f3edff 0%, #ece4ff 30%, #f5eef8 60%, #faf5f0 100%)',
  strong: 'linear-gradient(160deg, #ede5ff 0%, #e0d0ff 30%, #efe6f8 60%, #f5efea 100%)',
};

/* ---- Mini Calendar Component (Light Theme) ---- */
const MiniCalendar = ({ availableDates = [], onSelectDate, selectedDate }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthName = currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' });
  const availableSet = useMemo(() => new Set(availableDates), [availableDates]);

  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);

  const isAvailable = (day) => {
    if (!day) return false;
    return availableSet.has(`${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`);
  };
  const isSelected = (day) => {
    if (!day || !selectedDate) return false;
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}` === selectedDate;
  };
  const isPast = (day) => {
    if (!day) return false;
    const minDate = new Date(); minDate.setHours(0,0,0,0); minDate.setDate(minDate.getDate() + 7);
    return new Date(year, month, day) < minDate;
  };
  const isWeekend = (day) => {
    if (!day) return false;
    const dow = new Date(year, month, day).getDay();
    return dow === 0 || dow === 6;
  };

  return (
    <div data-testid="session-calendar" className="bg-white rounded-xl p-4 border border-purple-100/60 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => setCurrentMonth(new Date(year, month - 1))} className="p-1 rounded-full hover:bg-purple-50 text-gray-400 hover:text-purple-600 transition-colors"><ChevronLeft size={16} /></button>
        <span className="text-sm font-medium text-gray-700 tracking-wide">{monthName}</span>
        <button onClick={() => setCurrentMonth(new Date(year, month + 1))} className="p-1 rounded-full hover:bg-purple-50 text-gray-400 hover:text-purple-600 transition-colors"><ChevronRight size={16} /></button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-center mb-1">
        {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => (
          <div key={d} className={`text-[9px] font-medium py-1 ${d === 'Su' || d === 'Sa' ? 'text-red-300' : 'text-gray-400'}`}>{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {days.map((day, i) => {
          const available = isAvailable(day);
          const selected = isSelected(day);
          const past = isPast(day);
          const weekend = isWeekend(day);
          const disabled = !day || past || weekend || !available;
          return (
            <button key={i} disabled={disabled}
              onClick={() => {
                if (day && available && !weekend && !past) { const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`; onSelectDate(selectedDate === dateStr ? null : dateStr); };
              }}
              className={`h-8 w-full rounded-md text-[11px] transition-all ${
                !day ? '' :
                weekend ? 'text-red-200 cursor-not-allowed' :
                selected ? 'bg-purple-600 text-white font-bold shadow-md' :
                available && !past ? 'bg-purple-50 text-purple-700 hover:bg-purple-100 cursor-pointer font-medium' :
                past ? 'text-gray-200' : 'text-gray-300'
              }`}
            >{day || ''}</button>
          );
        })}
      </div>
      <div className="mt-2 flex items-center gap-3 text-[9px] text-gray-400">
        {availableDates.length > 0 && <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-purple-50 border border-purple-200" /> Available</span>}
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-50" /> Weekend</span>
        <span>Min 7 days advance</span>
      </div>
    </div>
  );
};

const SessionsSection = ({ sectionConfig }) => {
  const navigate = useNavigate();
  const { getPrice, formatPrice, baseCurrency } = useCurrency();
  const { settings } = useSiteSettings();
  const { addSessionItem } = useCart();
  const { toast } = useToast();
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [sessionCalendar, setSessionCalendar] = useState({});
  const sessionsListRef = useRef(null);

  const sessionTpl = settings?.page_heroes?.session_template || {};
  const purpleIntensity = sessionTpl.homepage_purple || 'medium';
  const purpleGradient = PURPLE_GRADIENTS[purpleIntensity] || PURPLE_GRADIENTS.medium;
  const buttonBg = sessionTpl.button_bg || '#D4AF37';
  const buttonText = sessionTpl.button_text || '#1a1a1a';

  // Offer helpers
  const isOfferActive = (session) => {
    const now = new Date().toISOString().split('T')[0];
    if (isEarlyBirdActive(session) && resolveEffectiveOffer(session, baseCurrency).price > 0) {
      return resolveEffectiveOffer(session, baseCurrency).text || 'Early Bird';
    }
    // Per-session offer
    if (session.offer_text && (!session.offer_expiry || session.offer_expiry >= now)) return session.offer_text;
    // Global offer
    if (sessionTpl.global_offer_text && (!sessionTpl.global_offer_expiry || sessionTpl.global_offer_expiry >= now)) return sessionTpl.global_offer_text;
    return null;
  };
  const getOfferPrice = (session) => {
    const resolved = resolveEffectiveOffer(session, baseCurrency);
    if (resolved.price <= 0) return 0;
    return getPrice({ ...session, [`price_${baseCurrency}`]: resolved.price });
  };
  const offerBadgeBg = sessionTpl.offer_badge_bg || '#ef4444';
  const offerBadgeText = sessionTpl.offer_badge_text || '#ffffff';

  // Visibility helpers — defaults match admin panel
  const HOMEPAGE_LIST_DEFAULTS = { session_name: true, session_type: false, duration: false };
  const vis = sessionTpl.visibility || {};
  const getVisible = (section, key) => {
    const items = vis[section];
    if (!items || !Array.isArray(items) || items.length === 0) {
      if (section === 'homepage_list') return HOMEPAGE_LIST_DEFAULTS[key] ?? true;
      return true;
    }
    const item = items.find(i => i.key === key);
    return item ? item.visible !== false : true;
  };
  const getOrder = (section) => {
    const items = vis[section];
    if (!items || !Array.isArray(items) || items.length === 0) return null;
    return items.filter(i => i.visible !== false).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map(i => i.key);
  };

  useEffect(() => { loadSessions(); }, []);

  useEffect(() => {
    axios.get(`${API}/session-extras/calendar`)
      .then((res) => setSessionCalendar(res.data || {}))
      .catch(() => setSessionCalendar({}));
  }, []);

  const loadSessions = async () => {
    try {
      const response = await axios.get(`${API}/sessions?visible_only=true`);
      if (response.data && response.data.length > 0) setSessions(response.data);
    } catch (error) { console.error('Error loading sessions:', error); }
  };

  const modeIcon = (mode) => {
    if (mode === 'offline') return <MapPin size={12} />;
    if (mode === 'both') return <><Wifi size={12} /><MapPin size={12} /></>;
    return <Wifi size={12} />;
  };
  const modeLabel = (mode) => {
    if (mode === 'offline') return 'Offline (Remote, Not In-Person)';
    if (mode === 'both') return 'Online (Zoom) & Offline';
    return 'Online (Zoom)';
  };

  return (
    <section id="sessions" data-testid="sessions-section" className="py-12 relative overflow-hidden"
      style={{ background: purpleGradient }}>

      {/* Smooth top fade from white into purple gradient */}
      <div className="absolute top-0 left-0 w-full h-28 z-[1] pointer-events-none"
        style={{ background: 'linear-gradient(180deg, #ffffff 0%, rgba(255,255,255,0.6) 30%, rgba(255,255,255,0) 100%)' }} />

      {/* Subtle gold shimmer line */}
      <div className="absolute top-0 left-0 right-0 h-[1px] opacity-20" style={{ background: 'linear-gradient(90deg, transparent 10%, #D4AF37 50%, transparent 90%)' }} />

      <div className={`${CONTAINER} relative z-10`}>
        {/* Section Title */}
        <div className="text-center mb-10">
          <h2 style={applySectionStyle(sectionConfig?.title_style, { ...HEADING, color: '#4c1d95', fontStyle: 'italic', fontSize: 'clamp(1.5rem, 3vw, 2rem)' })} data-testid="sessions-section-title">
            {sectionConfig?.title || 'Book Personal Session'}
          </h2>
          {sectionConfig?.subtitle && (
            <p className="text-sm mt-3 max-w-lg mx-auto" style={applySectionStyle(sectionConfig?.subtitle_style, { color: '#1a1a1a' })}>{sectionConfig.subtitle}</p>
          )}
          <div className="h-[2px] w-12 mx-auto mt-4" style={{ background: 'linear-gradient(90deg, transparent, #D4AF37, transparent)' }} />
        </div>

        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-8">

          {/* Left — Session List */}
          <aside className="w-full lg:w-[340px] lg:min-w-[340px] flex-shrink-0" data-testid="sessions-list">
            <div className="bg-white/70 backdrop-blur-sm rounded-xl border border-purple-100/50 shadow-sm overflow-hidden relative">
              {/* Custom always-visible purple-gold scrollbar */}
              <PurpleGoldScrollbar scrollRef={sessionsListRef} />
              <div
                ref={sessionsListRef}
                className="max-h-[520px] overflow-y-auto"
                style={{ scrollbarWidth: 'none', paddingRight: 10 }}
              >
                {sessions.map((session) => (
                  <button
                    key={session.id}
                    data-testid={`session-tab-${session.id}`}
                    onClick={() => { setSelectedSession(session); setSelectedDate(null); }}
                    className={`w-full text-left px-5 py-4 transition-all border-b border-purple-50 group ${
                      selectedSession?.id === session.id
                        ? 'bg-purple-50/80 border-l-[3px] border-l-purple-500'
                        : 'border-l-[3px] border-l-transparent hover:bg-purple-25 hover:bg-purple-50/30'
                    }`}
                  >
                    {getVisible('homepage_list', 'session_name') && (
                      <div className="flex items-center gap-2">
                        <span className={`block text-[13px] leading-snug ${
                          selectedSession?.id === session.id ? 'text-purple-900 font-semibold' : 'text-gray-700'
                        }`}
                          style={sessionTpl.title_style ? applySectionStyle(sessionTpl.title_style, { fontFamily: "'Lato', sans-serif" }) : { fontFamily: "'Lato', sans-serif" }}
                        >
                          {session.title}
                        </span>
                        {isOfferActive(session) && (
                          <span className="text-[7px] px-1.5 py-0.5 rounded-full font-bold whitespace-nowrap animate-pulse"
                            style={{ background: offerBadgeBg, color: offerBadgeText }}>
                            {isOfferActive(session)}
                          </span>
                        )}
                      </div>
                    )}
                    {(getVisible('homepage_list', 'session_type') || getVisible('homepage_list', 'duration')) && (
                      <div className="flex items-center gap-2 text-gray-400 text-[10px]">
                        {getVisible('homepage_list', 'session_type') && (
                          <span className="flex items-center gap-1">{modeIcon(session.session_mode)} {modeLabel(session.session_mode)}</span>
                        )}
                        {getVisible('homepage_list', 'duration') && session.duration && (
                          <span className="flex items-center gap-1"><Clock size={10} /> {session.duration}</span>
                        )}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          {/* Right — Details Panel */}
          <main className="flex-1 min-w-0">
            {!selectedSession ? (
              <div className="h-full flex items-center justify-center min-h-[400px]">
                <div className="text-center px-8">
                  <div className="w-20 h-20 mx-auto mb-5 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(139,92,246,0.12), rgba(212,175,55,0.08))' }}>
                    <CalendarIcon size={32} className="text-purple-400" />
                  </div>
                  <h3 className="text-purple-900/80 text-lg font-light mb-2" style={sessionTpl.placeholder_title_style ? applySectionStyle(sessionTpl.placeholder_title_style, { fontFamily: "'Playfair Display', serif" }) : { fontFamily: "'Playfair Display', serif" }}>
                    {sessionTpl.placeholder_title || 'Choose Your Healing Journey'}
                  </h3>
                  <p className="text-gray-400 text-sm max-w-xs mx-auto leading-relaxed" style={sessionTpl.placeholder_subtitle_style ? applySectionStyle(sessionTpl.placeholder_subtitle_style, {}) : {}}>
                    {sessionTpl.placeholder_subtitle || 'Select a session from the left to explore details, read testimonials, and book your personal appointment.'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="animate-fade-in space-y-6" data-testid={`session-detail-${selectedSession.id}`}>
                {(getOrder('homepage_detail') || ['session_type_badge','duration_badge','title','description','testimonial','price','calendar','time_slots','book_button']).map(key => {
                  if (key === 'session_type_badge' || key === 'duration_badge') {
                    // Render badges together on first encounter
                    if (key === 'duration_badge') return null; // handled with session_type_badge
                    const showType = getVisible('homepage_detail', 'session_type_badge');
                    const showDur = getVisible('homepage_detail', 'duration_badge');
                    if (!showType && !showDur) return null;
                    return (
                      <div key="badges" className="flex items-center gap-2">
                        {showType && (
                          <span className={`text-[10px] px-3 py-1 rounded-full font-medium flex items-center gap-1 ${
                            selectedSession.session_mode === 'offline' ? 'bg-teal-50 text-teal-600' :
                            selectedSession.session_mode === 'both' ? 'bg-purple-50 text-purple-600' :
                            'bg-blue-50 text-blue-600'
                          }`}>
                            {modeIcon(selectedSession.session_mode)} {modeLabel(selectedSession.session_mode)}
                          </span>
                        )}
                        {showDur && selectedSession.duration && (
                          <span className="text-[10px] px-3 py-1 rounded-full bg-gray-50 text-gray-500 flex items-center gap-1">
                            <Clock size={10} /> {selectedSession.duration}
                          </span>
                        )}
                      </div>
                    );
                  }
                  if (key === 'title') return (
                    <h3 key="title" className="text-gray-900 text-xl" style={sessionTpl.title_style ? applySectionStyle(sessionTpl.title_style, { fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 400, letterSpacing: '0.02em' }) : { fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 400, letterSpacing: '0.02em' }}
                      data-testid="selected-session-title">
                      {selectedSession.title}
                    </h3>
                  );
                  if (key === 'description') return (
                    <p key="desc" className="text-gray-500 text-sm leading-relaxed max-w-[600px]"
                      style={selectedSession.description_style ? applySectionStyle(selectedSession.description_style, {}) : { fontFamily: "'Lato', sans-serif", lineHeight: '1.85' }}
                      dangerouslySetInnerHTML={{ __html: renderMarkdown(selectedSession.description) }}
                      data-testid="selected-session-description" />
                  );
                  if (key === 'testimonial' && selectedSession.testimonial_text) return (
                    <div key="test" className="bg-gradient-to-br from-purple-50/60 to-amber-50/30 rounded-xl p-5 border border-purple-100/40 relative" data-testid="session-testimonial">
                      <Quote size={20} className="text-purple-200 absolute top-3 left-3" />
                      <p className="text-gray-600 text-[13px] leading-relaxed italic pl-6"
                        dangerouslySetInnerHTML={{ __html: renderMarkdown(selectedSession.testimonial_text) }} />
                    </div>
                  );
                  if (key === 'price') {
                    const offerPrc = getOfferPrice(selectedSession);
                    const originalPrc = getPrice(selectedSession);
                    const offerBadge = isOfferActive(selectedSession);
                    return (
                      <div key="price">
                        {selectedSession.pay_as_you_wish ? (
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <span className="text-2xl font-bold text-purple-700">Pay as you wish</span>
                            <span className="text-gray-400 text-xs w-full">per session</span>
                          </div>
                        ) : (
                          <>
                            {offerBadge && (
                              <span className="inline-block text-[9px] px-2.5 py-1 rounded-full font-bold mb-2 animate-pulse"
                                style={{ background: offerBadgeBg, color: offerBadgeText }} data-testid="offer-badge">
                                {offerBadge}
                              </span>
                            )}
                            {formatPrice(originalPrc) ? (
                              <div className="flex items-baseline gap-2 flex-wrap">
                                {offerPrc > 0 ? (
                                  <>
                                    <span className="text-2xl font-bold text-purple-700">{formatPrice(offerPrc)}</span>
                                    <span className="text-base text-gray-400 line-through">{formatPrice(originalPrc)}</span>
                                  </>
                                ) : (
                                  <span className="text-2xl font-bold text-purple-700">{formatPrice(originalPrc)}</span>
                                )}
                                <span className="text-gray-400 text-xs">per session</span>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400 italic">Contact for pricing</span>
                            )}
                          </>
                        )}
                      </div>
                    );
                  }
                  if (key === 'calendar') return (
                    <div key="cal">
                      <MiniCalendar
                        availableDates={sessionCalendar.available_dates?.length ? sessionCalendar.available_dates : (selectedSession.available_dates || [])}
                        selectedDate={selectedDate}
                        onSelectDate={(d) => { setSelectedDate(d); setSelectedTimeSlot(null); }}
                      />
                      <SessionOpenDatesSlots
                        calendar={sessionCalendar}
                        sessionFallback={selectedSession.time_slots || []}
                        selectedDate={selectedDate}
                        selectedTimeSlot={selectedTimeSlot}
                        onSelectDate={(d) => { setSelectedDate(d); setSelectedTimeSlot(null); }}
                        onSelectTimeSlot={setSelectedTimeSlot}
                        theme="light"
                      />
                    </div>
                  );
                  if (key === 'time_slots') {
                    const slots = timeSlotsForCalendarDate(
                      sessionCalendar,
                      selectedDate,
                      selectedSession.time_slots || [],
                    );
                    if (!selectedDate || slots.length === 0) return null;
                    return (
                    <div key="slots">
                      <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-2">Selected time</p>
                      <div className="flex flex-wrap gap-2">
                        {slots.map((slot, i) => (
                          <span key={i} onClick={() => setSelectedTimeSlot(slot)}
                            className={`px-3 py-1.5 rounded-full text-xs border cursor-pointer transition-all ${
                              selectedTimeSlot === slot ? 'bg-purple-600 text-white border-purple-600' : 'bg-purple-50 text-purple-600 border-purple-100 hover:bg-purple-100'
                            }`} data-testid={`time-slot-${i}`}>
                            {slot}
                          </span>
                        ))}
                      </div>
                    </div>
                  ); }
                  if (key === 'book_button') {
                    const slots = timeSlotsForCalendarDate(
                      sessionCalendar,
                      selectedDate,
                      selectedSession.time_slots || [],
                    );
                    const hasSlots = slots.length > 0;
                    const canBook = selectedDate && (!hasSlots || selectedTimeSlot);
                    return (
                    <div key="book-actions" className="space-y-2">
                      {!canBook && (
                        <p className="text-[10px] text-gray-400 text-center italic">
                          {!selectedDate ? 'Select a date to proceed' : 'Select a time slot to proceed'}
                        </p>
                      )}
                      <button onClick={() => navigate(`/session/${selectedSession.id}`)} data-testid="book-session-btn"
                        className="w-full py-3.5 rounded-full text-[11px] tracking-[0.2em] uppercase font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.02]"
                        style={{ background: `linear-gradient(135deg, ${buttonBg}, ${buttonBg}dd)`, color: buttonText }}>
                        View Details & Book
                      </button>
                      <button onClick={() => {
                          const added = addSessionItem(selectedSession, selectedDate, selectedTimeSlot);
                          if (added) toast({ title: `${selectedSession.title} added to cart` });
                          else toast({ title: 'Already in cart', variant: 'destructive' });
                        }}
                        data-testid="add-session-cart-btn"
                        disabled={!canBook}
                        className={`w-full py-3 rounded-full text-[11px] tracking-[0.15em] uppercase font-medium transition-all duration-300 border-2 flex items-center justify-center gap-2 ${
                          !canBook ? 'opacity-40 cursor-not-allowed' : 'hover:scale-[1.02]'
                        }`}
                        style={{ borderColor: buttonBg, color: buttonBg, background: 'transparent' }}>
                        <ShoppingCart size={14} /> Add to Cart
                      </button>
                    </div>
                  );}
                  return null;
                })}
              </div>
            )}
          </main>
        </div>
      </div>
    </section>
  );
};

export default SessionsSection;
